import { fetchUserAttributes } from "aws-amplify/auth";
import { useState } from 'react';
import { newHeatingSystem } from '../../../domain/data-collection/defaults';
import { aggregateElectricityInvoiceEntries } from '../../../domain/data-collection/electricityInvoiceAggregate';
import { parseElectricityInvoicePdf } from '../../../domain/data-collection/electricityInvoicePdf';
import { cleanNumericInput, formatNumericInput } from '../../../utils/numberInput';
import { readSdxValue } from '../../../utils/sdxHelpers';
import {
  Badge,
  FieldLabel,
  SdxField,
  ChoiceCard,
  ERROR_TEXT,
  Hint,
  SectionCard,
  UnitToggle,
} from '../UIComponents';


const BRAND = '#004A53';
const INTERACTION = '#026F6D';
const GREEN_SURFACE = '#f0f7f6';
const MUTED = '#64748b';

const HEATING_TYPES = [
  'Heizöl',
  'Erdgas',
  'Fernwärme',
  'Holz/Pellets',
  'Wärmepumpe',
  'Solarwärme',
];

const HEATING_UNIT_BY_TYPE = {
  'Heizöl': 'liter',
  'Erdgas': 'kwh',
  'Fernwärme': 'kwh',
  'Holz/Pellets': 'tonnen',
  'Wärmepumpe': 'kwh',
  'Solarwärme': 'kwh',
  'Andere': 'kwh',
  'Weiss nicht': 'unknown',
};

const ELECTRICITY_UNITS = [
  { value: 'kwh', label: 'kWh' },
];

const HEATING_UNITS = [
  { value: 'kwh', label: 'kWh' },
  { value: 'liter', label: 'Liter' },
  { value: 'tonnen', label: 'Tonnen' },
  { value: 'chf', label: 'CHF' },
];

function isCarbonPulseImported(formData, field) {
  return (
    Array.isArray(formData.carbonPulseImportedFields) &&
    formData.carbonPulseImportedFields.includes(field)
  );
}

async function uploadInvoicePDFToS3(file, fileName, folder) {
  if (!file) {
    throw new Error('Keine PDF-Datei zum Hochladen vorhanden');
  }

  try {
    const blob = new Blob([file], { type: 'application/pdf' });

    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-');

    const attributes = await fetchUserAttributes();

    const company = attributes?.["custom:company"] ?? "company";

    const s3FileName = `rechnung_${fileName || 'invoice'}_${company || 'company'}_${timestamp}.pdf`;



    // Signierte Upload-URL vom Lambda abrufen
    const query = new URLSearchParams({
      filename: s3FileName,
      folder: folder || 'sonstiges',
      contentType: 'application/pdf',
    });

    const res = await fetch(
      `https://3h7d6zdnlo6xp4l2shhw4zx6540eaeoo.lambda-url.eu-west-1.on.aws/?${query.toString()}`
    );

    if (!res.ok) {
      throw new Error(
        `Signierte Upload-URL konnte nicht geladen werden (${res.status})`
      );
    }

    const { uploadUrl } = await res.json();

    if (!uploadUrl) {
      throw new Error(
        'Upload-URL fehlt in der Serverantwort'
      );
    }

    // PDF zu S3 hochladen
    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: blob,
      headers: {
        'Content-Type': 'application/pdf',
      },
    });

    if (!uploadRes.ok) {
      throw new Error(
        `PDF-Upload fehlgeschlagen (${uploadRes.status})`
      );
    }

    return { success: true, fileName: s3FileName };
  } catch (error) {
    throw new Error(
      `Fehler beim Hochladen der PDF: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
    );
  }
}

export default function EnergyHeatingStep({
  formData,
  setField,
  setFormData,
  isReadOnly,
  fieldErrors,
}) {
  const pvStatus = formData.pvStatus;
  const [uploadProgress, setUploadProgress] = useState(null);
  const isParsingInvoice = uploadProgress != null;

  const invoiceFiles = Array.isArray(formData.electricityInvoiceFiles)
    ? formData.electricityInvoiceFiles
    : [];

  // OCR (the slow part) reports per-page progress via the "ocr:x/y" stage;
  // folding that into the bar keeps it moving instead of sitting still for
  // the whole file, which otherwise reads as a stuck/frozen upload.
  function invoiceProgressValue({ current, stage }) {
    const ocrMatch = stage?.match(/^ocr:(\d+)\/(\d+)$/);
    if (!ocrMatch) return current - 1;

    const [, page, pageCount] = ocrMatch;
    return current - 1 + Number(page) / Number(pageCount);
  }

  function applyInvoiceAggregate(previous, entries, applyValues = false) {
    const successful = entries.filter(entry => !entry.error && !entry.isPhoto);
    const aggregate = aggregateElectricityInvoiceEntries(
      successful,
      previous.reportingYear
    );

    const patch = {
      electricityInvoiceFiles: entries,
      electricityInvoiceWarnings: aggregate.warnings || [],
      electricityInvoiceHasBlockingIssues: Boolean(aggregate.hasBlockingIssues),
    };

    patch.electricityInvoiceConfirmed = false;

    if (previous.electricityInvoiceConfirmed && !applyValues) {
      patch.electricityValue = '';
    }

    if (applyValues && !aggregate.hasBlockingIssues) {
      if (aggregate.totalKwh != null) {
        patch.electricityValue = aggregate.totalKwh;
        patch.electricityUnit = 'kwh';
        patch.electricityInvoiceConfirmed = true;
      } else if (aggregate.totalChf != null) {
        patch.electricityValue = aggregate.totalChf;
        patch.electricityUnit = 'chf';
        patch.electricityInvoiceConfirmed = true;
      }
    }

    patch.electricityInvoicePeriodStart = aggregate.periodStart || '';
    patch.electricityInvoicePeriodEnd = aggregate.periodEnd || '';

    const parts = [];
    if (aggregate.totalKwh != null) {
      parts.push(
        `${aggregate.totalKwh.toLocaleString('de-CH')} kWh (${aggregate.kwhCount} Datei${aggregate.kwhCount === 1 ? '' : 'en'})`
      );
    }
    if (aggregate.totalChf != null) {
      parts.push(`CHF ${aggregate.totalChf.toLocaleString('de-CH')}`);
    }
    if (aggregate.periodStart && aggregate.periodEnd) {
      parts.push(`Periode ${aggregate.periodStart} – ${aggregate.periodEnd}`);
    }

    patch.electricityInvoiceImportMessage =
      parts.length > 0
        ? `Automatisch erkannt (Summe über ${successful.length} Datei${successful.length === 1 ? '' : 'en'}): ${parts.join(', ')}. Bitte prüfen.`
        : '';

    return { ...previous, ...patch };
  }

  async function handleElectricityInvoiceFiles(fileList) {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    const newEntries = [];

    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const id = `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`;

      setUploadProgress({
        total: files.length,
        current: index + 1,
        fileName: file.name,
        stage: 'reading',
      });

      if (file.name.split('.').pop()?.toLowerCase() !== 'pdf') {
        newEntries.push({
          id,
          fileName: file.name,
          isPhoto: true,
          kwh: null,
          chf: null,
          periodStart: null,
          periodEnd: null,
          error: null,
        });
        continue;
      }

      try {
        const result = await parseElectricityInvoicePdf(file, {
          onStatusChange: stage =>
            setUploadProgress(previous => ({ ...previous, stage })),
        });

        // Upload to S3
        try {
          await uploadInvoicePDFToS3(file, file.name.split('.')[0], 'rechnungen');
        } catch (uploadError) {
          console.warn('S3 upload warning:', uploadError);
        }

        newEntries.push({ id, fileName: file.name, error: null, ...result });
      } catch (error) {
        newEntries.push({
          id,
          fileName: file.name,
          kwh: null,
          chf: null,
          periodStart: null,
          periodEnd: null,
          error:
            error instanceof Error
              ? error.message
              : 'Die Rechnung konnte nicht automatisch ausgelesen werden.',
        });
        // Upload to S3
        try {
          await uploadInvoicePDFToS3(file, file.name.split('.')[0], 'nicht-unterstuetzte-rechnungen');
        } catch (uploadError) {
          console.warn('S3 upload warning:', uploadError);
        }
      }
    }

    setUploadProgress(null);

    setFormData(previous =>
      applyInvoiceAggregate(previous, [
        ...(Array.isArray(previous.electricityInvoiceFiles)
          ? previous.electricityInvoiceFiles
          : []),
        ...newEntries,
      ])
    );
  }

  function removeInvoiceFile(id) {
    setFormData(previous =>
      applyInvoiceAggregate(
        previous,
        invoiceFiles.filter(entry => entry.id !== id)
      )
    );
  }

  function updateInvoiceFile(id, patch) {
    setFormData(previous => {
      const entries = (
        Array.isArray(previous.electricityInvoiceFiles)
          ? previous.electricityInvoiceFiles
          : []
      ).map(entry =>
        entry.id === id
          ? { ...entry, ...patch }
          : entry
      );

      return applyInvoiceAggregate(previous, entries);
    });
  }

  function updateInvoiceNumber(id, field, value) {
    const cleaned = cleanNumericInput(value);
    const number = cleaned === '' ? null : Number(cleaned);

    updateInvoiceFile(id, {
      [field]: Number.isFinite(number) ? number : null,
      ...(field === 'chf' ? { chfNeedsReview: false } : {}),
    });
  }

  function confirmInvoiceImport() {
    setFormData(previous =>
      applyInvoiceAggregate(
        previous,
        Array.isArray(previous.electricityInvoiceFiles)
          ? previous.electricityInvoiceFiles
          : [],
        true
      )
    );
  }

  const carbonPulseElectricityImported = isCarbonPulseImported(
    formData,
    'electricityValue'
  );
  const pdfElectricityImported =
    Boolean(formData.electricityInvoiceConfirmed) &&
    invoiceFiles.some(
      entry =>
        !entry.error &&
        !entry.isPhoto &&
        (entry.kwh != null || entry.chf != null)
    );
  const electricityImported =
    carbonPulseElectricityImported || pdfElectricityImported;
  const pvImported = isCarbonPulseImported(formData, 'pvStatus');
  const heatingImported = isCarbonPulseImported(
    formData,
    'heatingSystems'
  );

  const heatingSystems =
    Array.isArray(formData.heatingSystems) &&
      formData.heatingSystems.length > 0
      ? formData.heatingSystems
      : [
        {
          id: 'legacy-heating-1',
          type: formData.heatingType || '',
          value: formData.heatingValue || '',
          unit: formData.heatingUnit || 'kwh',
        },
      ];

  function updateHeatingSystem(id, patch) {
    setField(
      'heatingSystems',
      heatingSystems.map(system =>
        system.id === id ? { ...system, ...patch } : system
      )
    );
  }

  function addHeatingSystem() {
    setField('heatingSystems', [
      ...heatingSystems,
      newHeatingSystem(),
    ]);
  }

  function removeHeatingSystem(id) {
    if (heatingSystems.length <= 1) return;
    const system = heatingSystems.find(item => item.id === id);
    if (system && (system.type || (system.value !== '' && system.value != null)) &&
        !window.confirm('Diese Heizart und die bereits erfassten Werte entfernen?')) return;

    setField(
      'heatingSystems',
      heatingSystems.filter(system => system.id !== id)
    );
  }

  return (
    <div>
      {/* Electricity */}
      <SectionCard title="Strom" badge="Pflicht">
        <div
          style={{
            marginBottom: '1.25rem',
            paddingBottom: 16,
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <label
            className="label"
            style={{ display: 'block', marginBottom: 8 }}
          >
            Stromrechnung(en) <Badge type="optional" />
          </label>

          <input
            id="electricity-upload"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            disabled={isReadOnly || isParsingInvoice}
            style={{ display: 'none' }}
            onChange={async event => {
              // event.target.files is a live FileList — copy it to a plain
              // array first, otherwise resetting .value below empties it
              // before handleElectricityInvoiceFiles ever reads it.
              const files = Array.from(event.target.files || []);
              event.target.value = '';

              if (files.length > 0) {
                await handleElectricityInvoiceFiles(files);
              }
            }}
          />

          <label
            htmlFor="electricity-upload"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '9px 14px',
              border: `1px dashed ${INTERACTION}`,
              borderRadius: 8,
              cursor: isReadOnly || isParsingInvoice ? 'default' : 'pointer',
              color: INTERACTION,
              background: '#ffffff',
              fontSize: 14,
              opacity: isParsingInvoice ? 0.6 : 1,
            }}
          >
            {isParsingInvoice
              ? `Datei ${uploadProgress.current}/${uploadProgress.total} wird verarbeitet…`
              : 'Datei(en) auswählen'}
          </label>

          {isParsingInvoice && (
            <div style={{ marginTop: 10 }}>
              <progress
                value={invoiceProgressValue(uploadProgress)}
                max={uploadProgress.total}
                style={{ width: '100%', height: 8, accentColor: INTERACTION }}
              />
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: 12,
                  color: MUTED,
                }}
              >
                {uploadProgress.fileName}
                {' — '}
                {uploadProgress.stage?.startsWith('ocr')
                  ? `Texterkennung (OCR) läuft… ${uploadProgress.stage.split(':')[1] || ''}`
                  : 'wird ausgelesen…'}
              </p>
            </div>
          )}
          {invoiceFiles.length > 0 && (
            <ul
              style={{
                listStyle: 'none',
                margin: '10px 0 0',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {invoiceFiles.map(entry => (
                <li
                  key={entry.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '7px 10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    fontSize: 13,
                    color: entry.error ? '#b91c1c' : '#334155',
                    background: entry.error ? '#fff1f2' : '#f8fafc',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <strong>{entry.fileName}</strong>

                      {entry.error
                        ? ` — ${entry.error}`
                        : entry.isPhoto
                          ? ' — Aktueller Datentyp wird nicht unterstützt - Bitte manuell eingeben'
                          : null}
                    </div>

                    {!entry.error && !entry.isPhoto && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr 1fr',
                          gap: 8,
                          marginTop: 8,
                        }}
                      >
                        <input
                          type="text"
                          value={entry.kwh ?? ''}
                          onChange={event =>
                            updateInvoiceNumber(entry.id, 'kwh', event.target.value)
                          }
                          disabled={isReadOnly || isParsingInvoice}
                          placeholder="kWh"
                          aria-label={`${entry.fileName} kWh`}
                        />

                        <input
                          type="text"
                          value={entry.chf ?? ''}
                          onChange={event =>
                            updateInvoiceNumber(entry.id, 'chf', event.target.value)
                          }
                          disabled={isReadOnly || isParsingInvoice}
                          placeholder="CHF exkl. MWST"
                          aria-label={`${entry.fileName} CHF exkl. MWST`}
                        />

                        <input
                          type="text"
                          value={entry.periodStart ?? ''}
                          onChange={event =>
                            updateInvoiceFile(entry.id, {
                              periodStart: event.target.value,
                            })
                          }
                          disabled={isReadOnly || isParsingInvoice}
                          placeholder="01.01.2025"
                          aria-label={`${entry.fileName} Periodenbeginn`}
                        />

                        <input
                          type="text"
                          value={entry.periodEnd ?? ''}
                          onChange={event =>
                            updateInvoiceFile(entry.id, {
                              periodEnd: event.target.value,
                            })
                          }
                          disabled={isReadOnly || isParsingInvoice}
                          placeholder="31.12.2025"
                          aria-label={`${entry.fileName} Periodenende`}
                        />
                      </div>
                    )}

                    {!entry.error && !entry.isPhoto && entry.chfNeedsReview && (
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 12,
                          color: '#b45309',
                        }}
                      >
                        Rechnungsbetrag konnte nicht eindeutig als CHF exkl. MWST erkannt
                        werden. Bitte kontrollieren und gegebenenfalls manuell ergänzen.
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeInvoiceFile(entry.id)}
                    disabled={isReadOnly || isParsingInvoice}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#94a3b8',
                      cursor: isReadOnly || isParsingInvoice ? 'default' : 'pointer',
                      fontSize: 16,
                      lineHeight: 1,
                      padding: 2,
                    }}
                    aria-label={`${entry.fileName} entfernen`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {formData.electricityInvoicePeriodStart &&
            formData.electricityInvoicePeriodEnd && (
              <p
                style={{
                  margin: '10px 0 0',
                  fontSize: 13,
                  color: MUTED,
                }}
              >
                Erkannte Gesamtperiode:{' '}
                <strong>
                  {formData.electricityInvoicePeriodStart} –{' '}
                  {formData.electricityInvoicePeriodEnd}
                </strong>
              </p>
            )}

          {formData.electricityInvoiceImportMessage && (
            <Hint type="success">
              {formData.electricityInvoiceImportMessage}
            </Hint>
          )}

          {Array.isArray(formData.electricityInvoiceWarnings) &&
            formData.electricityInvoiceWarnings.map((warning, index) => (
              <Hint key={`${warning}-${index}`} type="warning">
                {warning}
              </Hint>
            ))}


          {invoiceFiles.some(
            entry => !entry.error && !entry.isPhoto
          ) && (
              <div style={{ marginTop: 10 }}>
                <sdx-button
                  theme="primary"
                  label="Geprüfte Werte übernehmen"
                  onClick={confirmInvoiceImport}
                  disabled={
                    isReadOnly ||
                    isParsingInvoice ||
                    formData.electricityInvoiceHasBlockingIssues ||
                    undefined
                  }
                ></sdx-button>

                {formData.electricityInvoiceHasBlockingIssues && (
                  <p
                    style={{
                      margin: '6px 0 0',
                      fontSize: 12,
                      color: '#b91c1c',
                    }}
                  >
                    Die Werte können erst übernommen werden, wenn die markierten
                    Rechnungsperioden korrigiert wurden.
                  </p>
                )}
              </div>
            )}

          <Hint type="info">
            kWh, CHF und Abrechnungsperiode werden bei PDF-Rechnungen
            automatisch ausgelesen und aufsummiert. Bitte prüfen, dass alle
            hochgeladenen Rechnungen dieselbe Verbrauchsstelle betreffen und
            zusammen nicht mehr als ein Jahr abdecken. Für Fotos ist die
            Erfassung weiterhin manuell.
          </Hint>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label
            className="label"
            style={{ display: 'block', marginBottom: 8 }}
          >
            Strommix
          </label>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            {[
              {
                value: 'swiss_mix',
                label: 'Schweizer Mix',
                desc: 'Standard-Strommix',
              },
              {
                value: 'renewable',
                label: '100% Erneuerbar',
                desc: 'Erneuerbarer Strommix',
              },
              {
                value: 'own_mix',
                label: 'Eigener Mix',
                desc: 'Noch nicht verfügbar',
                disabled: true,
              },
              {
                value: 'unknown',
                label: 'Weiss nicht',
                desc: 'Kann aus der Stromrechnung ausgelesen werden',
              },
            ].map(option => (
              <ChoiceCard
                key={option.value}
                label={option.label}
                description={option.desc}
                selected={
                  formData.electricityMix === option.value
                }
                onClick={() =>
                  setField('electricityMix', option.value)
                }
                disabled={isReadOnly || option.disabled}
              />
            ))}
          </div>

          {fieldErrors?.electricityMix && (
            <span style={ERROR_TEXT}>
              {fieldErrors.electricityMix}
            </span>
          )}
        </div>

        {carbonPulseElectricityImported && !pdfElectricityImported && (
          <Hint type="success">
            Aus der Carbonpulse-Vorab-Checkliste übernommen. Werte können hier korrigiert werden.
          </Hint>
        )}

        {pdfElectricityImported && (
          <Hint type="success">
            Automatisch aus der Stromrechnung übernommen. Entfernen Sie die
            Datei(en) oben, um die Werte manuell zu erfassen.
          </Hint>
        )}

        <div
          style={{
            marginTop: electricityImported ? 16 : 0,
            marginBottom: '1rem',
            opacity: pdfElectricityImported ? 0.6 : 1,
          }}
        >
          <label
            className="label"
            style={{ display: 'block', marginBottom: 8 }}
          >
            Einheit Strombezug
          </label>

          <UnitToggle
            value={formData.electricityUnit}
            onChange={unit => {
              setField('electricityUnit', unit);

              if (unit === 'unknown') {
                setField('electricityValue', '');
              }
            }}
            options={ELECTRICITY_UNITS}
            disabled={isReadOnly || pdfElectricityImported}
          />
        </div>

        <div style={{ opacity: pdfElectricityImported ? 0.6 : 1 }}>
          {formData.electricityUnit === 'unknown' ? (
            <Hint type="info">
              Der Stromverbrauch ist nicht bekannt. Die Werte können
              später aus einer hochgeladenen Stromrechnung ausgelesen
              werden.
            </Hint>
          ) : (
            <>
              <SdxField
                label={`Jährlicher Strombezug (${formData.electricityUnit === 'chf'
                  ? 'CHF'
                  : 'kWh'
                  })`}
                type="text"
                inputmode="decimal"
                value={formatNumericInput(formData.electricityValue ?? '')}
                onInput={event =>
                  setField(
                    'electricityValue',
                    cleanNumericInput(event.target.value)
                  )
                }
                disabled={
                  isReadOnly || pdfElectricityImported || undefined
                }
                placeholder={
                  formData.electricityUnit === 'chf'
                    ? 'z. B. 11\'250'
                    : 'z. B. 45\'000'
                }
                valid={
                  fieldErrors?.electricityValue
                    ? 'false'
                    : undefined
                }
                validation-message={
                  fieldErrors?.electricityValue || undefined
                }
              ></SdxField>

              {formData.electricityUnit === 'chf' && (
                <p
                  style={{
                    margin: '6px 0 0',
                    fontSize: 12,
                    color: MUTED,
                  }}
                >
                  CHF pro Jahr gemäss Stromrechnung, exklusive MWST.
                </p>
              )}
            </>
          )}
        </div>
      </SectionCard>

      {/* PV */}
      <SectionCard title="Eigenproduktion PV" badge="Pflicht">
        {pvImported && (
          <Hint type="success">
            Aus der Carbonpulse-Vorab-Checkliste übernommen. Werte können hier korrigiert werden.
          </Hint>
        )}

        <div
          style={{
            marginTop: pvImported ? 16 : 0,
            opacity: 1,
          }}
        >
          <FieldLabel required>PV-Status</FieldLabel>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 10,
            }}
          >
            {[
              {
                value: 'vorhanden',
                label: 'PV vorhanden',
                description: 'eigene, ZEV/vZEV, LEG',
              },
              { value: 'keine', label: 'Keine PV' },
              { value: 'planung', label: 'In Planung' },
            ].map(option => (
              <ChoiceCard
                key={option.value}
                label={option.label}
                description={option.description}
                selected={pvStatus === option.value}
                onClick={() =>
                  setField(
                    'pvStatus',
                    pvStatus === option.value ? '' : option.value
                  )
                }
                disabled={isReadOnly}
              />
            ))}
          </div>
          {fieldErrors?.pvStatus && (
            <span style={ERROR_TEXT}>{fieldErrors.pvStatus}</span>
          )}
        </div>

        {pvStatus === 'vorhanden' && (
          <div style={{ marginTop: 16, opacity: 1 }}>
            <SdxField
              required={false}
              label="PV-Produktion (kWh/Jahr)"
              type="text"
              inputmode="decimal"
              value={formatNumericInput(formData.pvProductionKwh ?? '')}
              onInput={event =>
                setField(
                  'pvProductionKwh',
                  cleanNumericInput(event.target.value)
                )
              }
              disabled={isReadOnly || undefined}
              placeholder="z. B. 25'000"
              valid={fieldErrors?.pvProductionKwh ? 'false' : undefined}
              validation-message={fieldErrors?.pvProductionKwh || undefined}
            ></SdxField>

            <div style={{ marginTop: 12 }}>
              <SdxField
                required={false}
                label="Rückspeisung (kWh/Jahr)"
                type="text"
                inputmode="decimal"
                value={formatNumericInput(formData.pvFeedInKwh ?? '')}
                onInput={event =>
                  setField(
                    'pvFeedInKwh',
                    cleanNumericInput(event.target.value)
                  )
                }
                disabled={isReadOnly || undefined}
                placeholder="z. B. 8'000"
                valid={fieldErrors?.pvFeedInKwh ? 'false' : undefined}
                validation-message={fieldErrors?.pvFeedInKwh || undefined}
              ></SdxField>
            </div>
            {formData.pvProductionKwh && formData.pvFeedInKwh && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 14px',
                  background: GREEN_SURFACE,
                  borderRadius: 8,
                  fontSize: 14,
                  color: BRAND,
                }}
              >
                Geschätzter Eigenverbrauch:{' '}
                <strong>
                  {Math.max(
                    0,
                    (parseFloat(formData.pvProductionKwh) || 0) -
                    (parseFloat(formData.pvFeedInKwh) || 0)
                  ).toLocaleString('de-CH')}{' '}
                  kWh/Jahr
                </strong>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Heating */}
      <SectionCard title="Heizung Gebäude" badge="Pflicht">
        <div
          style={{
            marginBottom: '1.25rem',
            paddingBottom: 16,
            borderBottom: '1px solid #e2e8f0',
            opacity: 0.55,
          }}
        >
          <label
            className="label"
            style={{ display: 'block', marginBottom: 8 }}
          >
            Heizkostenrechnung oder Foto hochladen
            <span
              style={{
                marginLeft: 6,
                fontSize: 11,
                padding: '1px 7px',
                borderRadius: 999,
                background: '#f1f5f9',
                color: '#64748b',
                fontWeight: 600,
              }}
            >
              noch nicht verfügbar
            </span>
          </label>

          <input
            id="heating-upload"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            disabled
            style={{ display: 'none' }}
          />

          <label
            htmlFor="heating-upload"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '9px 14px',
              border: `1px dashed ${INTERACTION}`,
              borderRadius: 8,
              cursor: 'default',
              color: INTERACTION,
              background: '#ffffff',
              fontSize: 14,
            }}
          >
            Datei auswählen
          </label>

          <Hint type="info">
            Automatisches Auslesen von Heizkostenrechnungen ist noch nicht
            verfügbar (anders als bei der Stromrechnung oben). Bitte die
            Heizwerte unten weiterhin manuell erfassen.
          </Hint>
        </div>

        <Hint>
          Wird die Kabine separat beheizt, hier nur die
          Gebäudeheizung erfassen. Die Kabine wird im Schritt
          Lackieren erfasst.
        </Hint>

        {heatingImported && (
          <Hint type="success">
            Aus der Carbonpulse-Vorab-Checkliste übernommen. Werte können hier korrigiert werden.
          </Hint>
        )}

        <div style={{ marginTop: 16 }}>
          {heatingSystems.map((system, index) => {
            const isUnknown = system.unit === 'unknown';

            return (
              <div
                key={system.id}
                style={{
                  padding: '1.25rem',
                  marginBottom: 16,
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  background: '#f8fafc',
                  opacity: 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <h3
                    className="h3"
                    style={{
                      margin: 0,
                      fontSize: 15,
                      color: BRAND,
                    }}
                  >
                    Heizart {index + 1}
                  </h3>

                  {heatingSystems.length > 1 && (
                    <sdx-button
                      theme="cancel"
                      variant="ghost"
                      label="Entfernen"
                      onClick={() =>
                        removeHeatingSystem(system.id)
                      }
                      disabled={
                        isReadOnly || undefined
                      }
                    ></sdx-button>
                  )}
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 16,
                    marginBottom: '1rem',
                  }}
                >
                  <SdxField as="sdx-select"
                    label="Heizart"
                    placeholder="Auswählen…"
                    onInput={event => {
                      const nextType = readSdxValue(event);
                      const nextUnit = HEATING_UNIT_BY_TYPE[nextType] ?? system.unit;

                      updateHeatingSystem(system.id, {
                        type: nextType,
                        unit: nextUnit,
                        value: nextUnit === 'unknown' ? '' : system.value,
                      });
                    }}
                    disabled={
                      isReadOnly || undefined
                    }
                    valid={
                      fieldErrors?.[
                        `heatingSystems.${index}.type`
                      ]
                        ? 'false'
                        : undefined
                    }
                    validation-message={
                      fieldErrors?.[
                      `heatingSystems.${index}.type`
                      ] || undefined
                    }
                  >
                    {HEATING_TYPES.map(type => (
                      <sdx-select-option
                        key={type}
                        value={type}
                        selected={system.type === type}
                      >
                        {type}
                      </sdx-select-option>
                    ))}
                  </SdxField>

                  <div>
                    <label
                      className="label"
                      style={{
                        display: 'block',
                        marginBottom: 8,
                      }}
                    >
                      Einheit
                    </label>

                    {(() => {
                      const correctUnit = HEATING_UNIT_BY_TYPE[system.type] ?? 'kwh';
                      const disabledUnits = HEATING_UNITS
                        .filter(u => u.value !== correctUnit && u.value !== 'unknown')
                        .map(u => u.value);

                      return (
                        <UnitToggle
                          value={system.unit}
                          onChange={unit =>
                            updateHeatingSystem(system.id, {
                              unit,
                              value:
                                unit === 'unknown'
                                  ? ''
                                  : system.value,
                            })
                          }
                          options={HEATING_UNITS}
                          disabled={isReadOnly}
                          disabledOptions={disabledUnits}
                        />
                      );
                    })()}
                  </div>
                </div>

                {isUnknown ? (
                  <Hint type="info">
                    Der Heizverbrauch ist nicht bekannt. Die Werte
                    können später aus einer hochgeladenen
                    Heizkostenrechnung ausgelesen werden.
                  </Hint>
                ) : (
                  <>
                    <SdxField
                      label={`Jährlicher Heizverbrauch (${system.unit === 'liter'
                        ? 'Liter'
                        : system.unit === 'tonnen'
                          ? 'Tonnen'
                          : system.unit === 'chf'
                            ? 'CHF'
                            : 'kWh'
                        })`}
                      type="text"
                      inputmode="decimal"
                      value={formatNumericInput(system.value ?? '')}
                      onInput={event =>
                        updateHeatingSystem(system.id, {
                          value: cleanNumericInput(event.target.value),
                        })
                      }
                      disabled={
                        isReadOnly || undefined
                      }
                      placeholder={
                        system.unit === 'liter'
                          ? 'z. B. 60\'000'
                          : system.unit === 'Tonnen'
                            ? 'z. B. 25'
                            : system.unit === 'chf'
                              ? 'z. B. 12\'500'
                              : 'z. B. 45\'000'
                      }
                      valid={
                        fieldErrors?.[
                          `heatingSystems.${index}.value`
                        ]
                          ? 'false'
                          : undefined
                      }
                      validation-message={
                        fieldErrors?.[
                        `heatingSystems.${index}.value`
                        ] || undefined
                      }
                    ></SdxField>

                    {system.unit === 'chf' && (
                      <p
                        style={{
                          margin: '6px 0 0',
                          fontSize: 12,
                          color: MUTED,
                        }}
                      >
                        CHF pro Jahr gemäss Heizkostenrechnung,
                        exklusive MWST.
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <sdx-button
          theme="secondary"
          label="+ Weitere Heizart hinzufügen"
          onClick={addHeatingSystem}
          disabled={isReadOnly || undefined}
        ></sdx-button>

        <div
          style={{
            marginTop: 20,
            paddingTop: 18,
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: isReadOnly ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={Boolean(formData.gasShutdownPlanned)}
              onChange={event => {
                setField(
                  'gasShutdownPlanned',
                  event.target.checked
                );

                if (!event.target.checked) {
                  setField('gasShutdownYear', '');
                }
              }}
              disabled={isReadOnly}
              style={{
                accentColor: INTERACTION,
              }}
            />

            <span>Gasnetzabschaltung geplant</span>
            <span aria-hidden="true"><Badge type="optional" /></span>
          </label>

          {formData.gasShutdownPlanned && (
            <div style={{ marginTop: 10, maxWidth: 320 }}>
              <SdxField
                label="Geplantes Jahr der Gasnetzabschaltung"
                type="number"
                value={formData.gasShutdownYear ?? ''}
                onInput={event =>
                  setField(
                    'gasShutdownYear',
                    event.target.value
                  )
                }
                disabled={isReadOnly || undefined}
                placeholder="z. B. 2030"
                valid={fieldErrors?.gasShutdownYear ? 'false' : undefined}
                validation-message={
                  fieldErrors?.gasShutdownYear || undefined
                }
              ></SdxField>
            </div>
          )}

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 18,
              cursor: isReadOnly ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={Boolean(
                formData.oilHeatingReplacementPlanned
              )}
              onChange={event => {
                setField(
                  'oilHeatingReplacementPlanned',
                  event.target.checked
                );

                if (!event.target.checked) {
                  setField('oilHeatingReplacementYear', '');
                }
              }}
              disabled={isReadOnly}
              style={{
                accentColor: INTERACTION,
              }}
            />

            <span>Ersatz der Ölheizung geplant</span>
            <span aria-hidden="true"><Badge type="optional" /></span>
          </label>

          {formData.oilHeatingReplacementPlanned && (
            <div style={{ marginTop: 10, maxWidth: 320 }}>
              <SdxField
                label="Geplantes Jahr des Heizungsersatzes"
                type="number"
                value={
                  formData.oilHeatingReplacementYear ?? ''
                }
                onInput={event =>
                  setField(
                    'oilHeatingReplacementYear',
                    event.target.value
                  )
                }
                disabled={isReadOnly || undefined}
                placeholder="z. B. 2028"
                valid={
                  fieldErrors?.oilHeatingReplacementYear
                    ? 'false'
                    : undefined
                }
                validation-message={
                  fieldErrors?.oilHeatingReplacementYear || undefined
                }
              ></SdxField>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
